import {
  EVIDENCE_FILE_EXTENSIONS,
  type EvidenceMediaType,
  positionCodeSchema,
  uuidSchema,
} from '@opti-sea/core';

/**
 * Storage paths of RISQ evidence (PLAN §2.8, D8, D25) in the private bucket `evidence`:
 * `{company_id}/{vessel_imo}/risq/{question_code}/{YYYY-MM-DD}_{position}_{evidence_id}.{jpg|mp4}`
 * and the thumbnail `…_{evidence_id}_thumb.jpg`. The date is the UTC date the photo was taken.
 * The server's Storage policies parse the same shape (`private.risq_evidence_path`); a path that
 * does not match is refused, so this module checks it before anything is written.
 */

export interface EvidencePathInput {
  companyId: string;
  imo: string;
  questionCode: string;
  /** UTC ISO time the photo or video was taken. */
  takenAt: string;
  /** Position of the person who took it (must be the uploader's own position, D44). */
  position: string;
  evidenceId: string;
  mediaType: EvidenceMediaType;
}

export interface EvidencePaths {
  storagePath: string;
  thumbPath: string;
}

/** Same pattern as the server's `private.risq_evidence_path` (Storage policies). */
export const EVIDENCE_PATH_PATTERN =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([0-9]{7})\/risq\/([^/]+)\/([0-9]{4}-[0-9]{2}-[0-9]{2})_([A-Z0-9]{1,12})_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(_thumb\.jpg|\.jpg|\.mp4)$/;

export function evidencePaths(input: EvidencePathInput): EvidencePaths {
  const evidenceId = uuidSchema.parse(input.evidenceId).toLowerCase();
  const companyId = uuidSchema.parse(input.companyId).toLowerCase();
  const position = positionCodeSchema.parse(input.position);
  if (!/^[0-9]{7}$/.test(input.imo)) {
    throw new Error(`evidencePaths: IMO must have 7 digits, got "${input.imo}"`);
  }
  if (input.questionCode === '' || /[/\s]/.test(input.questionCode)) {
    throw new Error(`evidencePaths: unusable question code "${input.questionCode}"`);
  }
  const taken = new Date(input.takenAt);
  if (Number.isNaN(taken.getTime())) {
    throw new Error(`evidencePaths: invalid time "${input.takenAt}"`);
  }
  const day = taken.toISOString().slice(0, 10);
  const base = `${companyId}/${input.imo}/risq/${input.questionCode}/${day}_${position}_${evidenceId}`;
  return {
    storagePath: `${base}.${EVIDENCE_FILE_EXTENSIONS[input.mediaType]}`,
    thumbPath: `${base}_thumb.jpg`,
  };
}
